import moment, { unix } from 'moment-timezone';
import { TIME_ZONE } from 'src/stores/userInterfaceSettings/localStorageKeys';
import { TimeZone } from 'timezones-list';

import { DateRangeSelection } from 'src/models/common';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { StatusButton } from 'src/models/status';
import { StatusEnum } from 'src/models/status/enums';

import { formatLongDate } from 'src/util/date';
import { getDateFormatKey, momentDatePattern, momentDatePatternWithShortYear } from 'src/util/date/dateFormatOptions';

export function initials(contact: string): string {
	const words = contact.split(/[-\s@]+/);
	return words
		.slice(0, 2)
		.map((word) => word.substr(0, 1))
		.join('')
		.substr(0, 3)
		.toUpperCase();
}

function timeZone(): string {
	const timeZoneFromLocalStorage =
		localStorage.getItem(TIME_ZONE) !== null
			? (JSON.parse(localStorage.getItem(TIME_ZONE) as string) as 'auto' | TimeZone)
			: 'auto';

	return timeZoneFromLocalStorage === 'auto' ? moment.tz.guess() : timeZoneFromLocalStorage.tzCode;
}

function formatDatePortion(m: moment.Moment): string {
	const pattern = momentDatePattern(getDateFormatKey());
	return pattern === null ? formatLongDate(m.toDate(), m.tz()) : m.format(pattern);
}

export function date(timestamp: number): string {
	const today = moment(new Date(), timeZone());
	const m = unix(timestamp).tz(timeZone());

	if (m.isSame(today, 'd')) {
		return m.format('HH:mm');
	}
	return formatDatePortion(m);
}

export function justDate(timestamp: number): string {
	return formatDatePortion(unix(timestamp).tz(timeZone()));
}

export function time(timestamp: number): string {
	return unix(timestamp).tz(timeZone()).format('HH:mm');
}

export function dateTime(timestamp: number): string {
	const today = moment(new Date(), timeZone());
	const m = unix(timestamp).tz(timeZone());

	if (m.isSame(today, 'd')) {
		return m.format('HH:mm');
	}
	return `${formatDatePortion(m)} ${m.format('HH:mm')}`;
}

export function dateTimeWithShortYear(timestamp: number): string {
	const today = moment(new Date(), timeZone());
	const m = unix(timestamp).tz(timeZone());

	if (m.isSame(today, 'd')) {
		return m.format('HH:mm');
	}
	const pattern = momentDatePatternWithShortYear(getDateFormatKey());
	const datePortion = pattern === null ? formatLongDate(m.toDate(), m.tz()) : m.format(pattern);
	return `${datePortion} ${m.format('HH:mm')}`;
}

export function dateTimeFull(timestamp: number): string {
	const m = unix(timestamp).tz(timeZone());
	return `${formatDatePortion(m)} ${m.format('HH:mm')}`;
}

export function dateInTimezoneEuropeBerlin(date: string): string {
	const momentInSourceTimeZone = moment.tz(date, timeZone());
	return momentInSourceTimeZone.tz('Europe/Berlin').format('YYYY-MM-DD HH:mm:ss');
}

export function dateInTimezoneFromTimeZoneEuropeBerlin(date: string): string {
	const momentInSourceTimeZone = moment.tz(date, 'Europe/Berlin');
	return momentInSourceTimeZone.tz(timeZone()).format('YYYY-MM-DD HH:mm:ss');
}

export function latestTimestampOfToday(): number {
	return moment(new Date()).tz('Europe/Berlin', false).endOf('day').unix();
}

function today(): string {
	return moment(new Date()).tz(timeZone(), false).endOf('day').format('YYYY-MM-DD');
}

function subtractOneDay(referenceDate: Date): string {
	return moment(referenceDate).tz(timeZone(), false).subtract(1, 'days').format('YYYY-MM-DD');
}

function addOneDay(referenceDate: Date): string {
	return moment(referenceDate).tz(timeZone(), false).add(1, 'day').format('YYYY-MM-DD');
}

function subtractOneWeek(referenceDate: Date): DateRangeSelection {
	return {
		from: moment(referenceDate).tz(timeZone(), false).subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'),
		to: moment(referenceDate).tz(timeZone(), false).subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD'),
	};
}

function addOneWeek(referenceDate: Date): DateRangeSelection {
	return {
		from: moment(referenceDate).tz(timeZone(), false).add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'),
		to: moment(referenceDate).tz(timeZone(), false).add(1, 'week').endOf('isoWeek').format('YYYY-MM-DD'),
	};
}

function thisWeek(): DateRangeSelection {
	return {
		from: moment(new Date()).tz(timeZone(), false).startOf('isoWeek').format('YYYY-MM-DD'),
		to: moment(new Date()).tz(timeZone(), false).endOf('isoWeek').format('YYYY-MM-DD'),
	};
}

function subtractFourWeeks(referenceDate: Date): DateRangeSelection {
	return {
		from: moment(referenceDate).tz(timeZone(), false).subtract(27, 'days').startOf('isoWeek').format('YYYY-MM-DD'),
		to: moment(referenceDate).tz(timeZone(), false).format('YYYY-MM-DD'),
	};
}

function subtractOneMonth(referenceDate: Date): DateRangeSelection {
	return {
		from: moment(referenceDate).tz(timeZone(), false).subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
		to: moment(referenceDate).tz(timeZone(), false).subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
	};
}

function addOneMonth(referenceDate: Date): DateRangeSelection {
	return {
		from: moment(referenceDate).tz(timeZone(), false).add(1, 'month').startOf('month').format('YYYY-MM-DD'),
		to: moment(referenceDate).tz(timeZone(), false).add(1, 'month').endOf('month').format('YYYY-MM-DD'),
	};
}

function addFourWeeks(referenceDate: Date): DateRangeSelection {
	return {
		from: moment(referenceDate).tz(timeZone(), false).add(4, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
		to: moment(referenceDate).tz(timeZone(), false).add(8, 'weeks').format('YYYY-MM-DD'),
	};
}

function lastFourWeeks(): DateRangeSelection {
	return {
		from: moment(new Date()).tz(timeZone(), false).subtract(4, 'weeks').format('YYYY-MM-DD'),
		to: moment(new Date()).tz(timeZone(), false).format('YYYY-MM-DD'),
	};
}

function thisMonth(): DateRangeSelection {
	return {
		from: moment(new Date()).tz(timeZone(), false).startOf('month').format('YYYY-MM-DD'),
		to: moment(new Date()).tz(timeZone(), false).endOf('month').format('YYYY-MM-DD'),
	};
}

function lastMonth(): DateRangeSelection {
	return {
		from: moment(new Date()).tz(timeZone(), false).subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
		to: moment(new Date()).tz(timeZone(), false).subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
	};
}

export function previousDateOrDateRangeSelectionForPreconfiguredRange(
	preconfiguredRange: PreconfiguredTimeRanges,
	referenceDate: Date
): DateRangeSelection | string {
	switch (preconfiguredRange) {
		case PreconfiguredTimeRanges.ALL:
			return moment(referenceDate).tz(timeZone(), false).format('YYYY-MM-DD');
		case PreconfiguredTimeRanges.TODAY:
		case PreconfiguredTimeRanges.YESTERDAY:
			return subtractOneDay(referenceDate);

		case PreconfiguredTimeRanges.THIS_WEEK:
		case PreconfiguredTimeRanges.LAST_WEEK:
			return subtractOneWeek(referenceDate);

		case PreconfiguredTimeRanges.LAST_FOUR_WEEKS:
			return subtractFourWeeks(referenceDate);

		case PreconfiguredTimeRanges.THIS_MONTH:
		case PreconfiguredTimeRanges.LAST_MONTH:
			return subtractOneMonth(referenceDate);
	}
}

export function nextDateOrDateRangeSelectionForPreconfiguredRange(
	preconfiguredRange: PreconfiguredTimeRanges,
	referenceDate: Date
): DateRangeSelection | string {
	switch (preconfiguredRange) {
		case PreconfiguredTimeRanges.ALL:
			return moment(referenceDate).tz(timeZone(), false).format('YYYY-MM-DD');
		case PreconfiguredTimeRanges.TODAY:
		case PreconfiguredTimeRanges.YESTERDAY:
			return addOneDay(referenceDate);

		case PreconfiguredTimeRanges.THIS_WEEK:
		case PreconfiguredTimeRanges.LAST_WEEK:
			return addOneWeek(referenceDate);

		case PreconfiguredTimeRanges.LAST_FOUR_WEEKS:
			return addFourWeeks(referenceDate);

		case PreconfiguredTimeRanges.THIS_MONTH:
		case PreconfiguredTimeRanges.LAST_MONTH:
			return addOneMonth(referenceDate);
	}
}

export function dateOrDateRangeSelectionForPreconfiguredRange(
	preconfiguredRange: PreconfiguredTimeRanges,
	referenceDate: Date
): DateRangeSelection | string {
	switch (preconfiguredRange) {
		case PreconfiguredTimeRanges.ALL:
			return moment(referenceDate).tz(timeZone(), false).format('YYYY-MM-DD');
		case PreconfiguredTimeRanges.TODAY:
			return today();
		case PreconfiguredTimeRanges.YESTERDAY:
			return subtractOneDay(new Date());
		case PreconfiguredTimeRanges.THIS_WEEK:
			return thisWeek();
		case PreconfiguredTimeRanges.LAST_WEEK:
			return subtractOneWeek(referenceDate);
		case PreconfiguredTimeRanges.LAST_FOUR_WEEKS:
			return lastFourWeeks();
		case PreconfiguredTimeRanges.THIS_MONTH:
			return thisMonth();
		case PreconfiguredTimeRanges.LAST_MONTH:
			return lastMonth();
	}
}

export function stateColor(statusButton: StatusButton) {
	switch (statusButton.status) {
		case StatusEnum.UNSEEN:
			return 'red';

		case StatusEnum.SEEN:
			return 'yellow-8';

		case StatusEnum.READY:
			return 'green-14';
	}
}

export function stateColorForStatusString(status: string): string {
	switch (status) {
		case StatusEnum.UNSEEN:
			return 'red';

		case StatusEnum.SEEN:
			return 'yellow-8';

		case StatusEnum.READY:
			return 'green-14';
	}

	return 'red';
}

export function textColorForStatusString(status: string): string {
	switch (status) {
		case StatusEnum.UNSEEN:
			return 'white';

		case StatusEnum.SEEN:
			return 'lightBlack';

		case StatusEnum.READY:
			return 'white';
	}

	return 'white';
}

export function statusColorHex(status: StatusEnum): string {
	switch (status) {
		case StatusEnum.UNSEEN:
			return 'var(--color-status-unseen)';

		case StatusEnum.SEEN:
			return 'var(--color-status-seen)';

		case StatusEnum.READY:
			return 'var(--color-status-ready)';
	}

	return 'var(--color-status-ready)';
}

export function stateColorHex(statusButton: StatusButton): string {
	return statusColorHex(statusButton.status);
}

export function hashCode(stringToHash: string) {
	let hash = 0;
	for (let i = 0; i < stringToHash.length; i++) {
		const char = stringToHash.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash;
	}
	return hash;
}
